import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { subscriptionService, useSubscriptionStatus } from '../services/subscriptionService';
import { personalizedPlanService } from '../services/personalizedPlanService';
import { ttsService } from '../services/ttsService'; // Add optimized TTS service
import { useAuth } from '../hooks/useAuth'; // Add useAuth hook
import { createSpeechSynthesis } from '../utils/SpeechSynthesis'; // Add React TTS for personal plan
import {
  Container,
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  Button,
  IconButton,
  Chip,
  Paper,
  Avatar,
  Stack,
  Divider,
  LinearProgress,
  Zoom,
  Fade,
  Grow,
  Alert,
  Tooltip,
  CircularProgress,
  List,
  ListItem,
  ListItemIcon,
  ListItemText
} from '@mui/material';
import {
  ArrowBack,
  Star,
  FlashOn,
  EmojiEvents,
  Diamond,
  AutoAwesome,
  CheckCircle,
  Psychology,
  Favorite,
  TrendingUp,
  Security,
  Support,
  AllInclusive,
  Warning,
  PlayArrow,
  Pause,
  VolumeUp,
  VolumeOff
} from '@mui/icons-material';

const Subscribe = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { profile } = useAuth(); // Get profile to check quiz completion status
  const [selectedPlan, setSelectedPlan] = useState('yearly'); // Default to yearly plan
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const { subscriptionStatus, tokenInfo, loading: statusLoading } = useSubscriptionStatus();
  
  // Personalized plan state
  const [personalizedPlan, setPersonalizedPlan] = useState(null);
  const [planLoading, setPlanLoading] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [audioLoading, setAudioLoading] = useState(false);
  
  // Add optimized audio states (keeping for compatibility)
  const [audioSupported, setAudioSupported] = useState(true);
  const [audioGenerationInProgress, setAudioGenerationInProgress] = useState(false);

  // React TTS states for personal transformation plan
  const speechSynthesis = useRef(null);
  const [isSpeakingPlan, setIsSpeakingPlan] = useState(false);
  const [reactTtsSupported, setReactTtsSupported] = useState(true);

  // State to track if we've refreshed after quiz completion
  const [hasRefreshed, setHasRefreshed] = useState(false);

  // Get quiz data from navigation state or sessionStorage as fallback
  // Add state to trigger re-computation of quiz data
  const [sessionStorageData, setSessionStorageData] = useState(null);
  
  // Check sessionStorage on mount and when location changes
  useEffect(() => {
    const storageData = sessionStorage.getItem('completedQuizData');
    if (storageData) {
      console.log('📥 Found quiz data in sessionStorage');
      setSessionStorageData(JSON.parse(storageData));
    }
  }, [location.pathname]);

  // Initialize React TTS for personal transformation plan
  useEffect(() => {
    speechSynthesis.current = createSpeechSynthesis();
    setReactTtsSupported(speechSynthesis.current.checkSupport());
    
    // Set up TTS callbacks
    speechSynthesis.current.onStart(() => {
      console.log('🎤 React TTS: Started speaking personal plan');
      setIsSpeakingPlan(true);
      setIsPlaying(true);
      setAudioLoading(false);
    });
    
    speechSynthesis.current.onEnd(() => {
      console.log('🔇 React TTS: Finished speaking personal plan');
      setIsSpeakingPlan(false);
      setIsPlaying(false);
      setAudioGenerationInProgress(false);
    });
    
    speechSynthesis.current.onError((error) => {
      console.error('❌ React TTS Error:', error);
      setIsSpeakingPlan(false);
      setIsPlaying(false);
      setAudioLoading(false);
      setAudioGenerationInProgress(false);
    });

    return () => {
      if (speechSynthesis.current?.checkSupport()) {
        speechSynthesis.current.stop();
      }
    };
  }, []);

  // Refresh page once when navigating to subscribe page after completing quiz
  useEffect(() => {
    const shouldRefresh = () => {
      // Check if we have quiz data (either from navigation state or sessionStorage)
      const hasQuizData = location.state?.quizData || sessionStorage.getItem('completedQuizData');
      
      // Check if we haven't refreshed yet
      const refreshed = sessionStorage.getItem('subscribe_page_refreshed');
      
      return hasQuizData && !refreshed && !hasRefreshed;
    };

    if (shouldRefresh()) {
      console.log('� Refreshing subscribe page after quiz completion...');
      
      // Mark as refreshed to prevent infinite refresh loops
      sessionStorage.setItem('subscribe_page_refreshed', 'true');
      setHasRefreshed(true);
      
      // Small delay to ensure all state is set before refresh
      setTimeout(() => {
        window.location.reload();
      }, 100);
    }
    
    // Clean up refresh flag when leaving the page
    return () => {
      // Only clean up if we're actually leaving the subscribe page
      if (!location.pathname.includes('/subscribe')) {
        sessionStorage.removeItem('subscribe_page_refreshed');
      }
    };
  }, [location.state?.quizData, sessionStorageData, hasRefreshed, location.pathname]);

  const quizData = useMemo(() => {
    const locationData = location.state?.quizData;
    const storageData = sessionStorageData || JSON.parse(sessionStorage.getItem('completedQuizData') || '{}');
    
    console.log('🔍 QuizData computation:', {
      hasLocationData: !!locationData,
      hasStorageData: !!storageData?.quizAnswers,
      locationAnswers: locationData?.quizAnswers?.length || 0,
      storageAnswers: storageData?.quizAnswers?.length || 0
    });
    
    return locationData || storageData;
  }, [location.state?.quizData, sessionStorageData]);

  console.log('📊 Subscribe: Quiz data source:', {
    fromLocationState: !!location.state?.quizData,
    fromSessionStorage: !!sessionStorage.getItem('completedQuizData'),
    hasQuizAnswers: !!quizData?.quizAnswers,
    quizAnswersLength: quizData?.quizAnswers?.length || 0,
    actualQuizData: quizData,
    rawSessionStorage: sessionStorage.getItem('completedQuizData')
  });

  // Debug function to add test quiz data (development only)
  if (process.env.NODE_ENV === 'development') {
    window.addTestQuizData = () => {
      const testQuizData = {
        quizAnswers: [
          {
            question: "What brings you to therapy today?",
            answer: ["Anxiety and stress", "Relationship issues"]
          },
          {
            question: "How would you describe your current mental state?",
            answer: "I've been feeling overwhelmed and anxious lately. Work stress is affecting my relationships."
          },
          {
            question: "What would you like to achieve through therapy?",
            answer: ["Better stress management", "Improved relationships", "More confidence"]
          },
          {
            question: "On a scale of 1-10, how motivated are you to make changes?",
            answer: "9"
          },
          {
            question: "What's your biggest challenge right now?",
            answer: "Managing anxiety while maintaining healthy relationships with my partner and family."
          }
        ],
        completedAt: new Date().toISOString(),
        userId: "test-user"
      };
      
      sessionStorage.setItem('completedQuizData', JSON.stringify(testQuizData));
      console.log('✅ Test quiz data added to sessionStorage');
      window.location.reload(); // Reload to trigger quiz data detection
    };
    
    window.clearQuizData = () => {
      sessionStorage.removeItem('completedQuizData');
      sessionStorage.removeItem('quizAnswers');
      console.log('❌ Quiz data cleared from sessionStorage');
      window.location.reload();
    };
    
    console.log('🛠️ Debug functions available:');
    console.log('   window.addTestQuizData() - Add test quiz data');
    console.log('   window.clearQuizData() - Clear quiz data');
  }

  const generatePersonalizedPlan = async () => {
    console.log('🎯 generatePersonalizedPlan called with quizData:', quizData);
    
    if (!quizData?.quizAnswers) {
      console.log('❌ No quiz data available for personalized plan generation:', {
        quizData: !!quizData,
        quizAnswers: !!quizData?.quizAnswers,
        sessionStorage: sessionStorage.getItem('completedQuizData')
      });
      return;
    }
    
    console.log('✅ Quiz data found, starting plan generation...');
    console.log('📊 Quiz answers to process:', quizData.quizAnswers);
    
    setPlanLoading(true);
    try {
      console.log('🚀 Generating personalized plan with quiz data:', quizData.quizAnswers);
      const plan = await personalizedPlanService.generatePersonalizedPlan(quizData.quizAnswers);
      console.log('✅ Generated plan received in component:', plan);
      setPersonalizedPlan(plan);
      
      // Skip pre-generation for now to avoid validation issues
      // The play button will generate audio on-demand instead
      console.log('ℹ️ Skipping audio pre-generation - will generate on-demand when play button is clicked');
    } catch (error) {
      console.error('❌ Error generating personalized plan in component:', error);
      console.error('❌ Error details:', error.message);
      console.error('❌ Error stack:', error.stack);
      
      // Set enhanced fallback plan with more specific personalization based on quiz
      console.log('⚠️ Setting enhanced fallback plan in component');
      
      // Try to extract some personalization from quiz answers
      let specificConcern = 'personal growth';
      let userExample = 'your responses';
      
      if (quizData.quizAnswers?.length > 0) {
        const firstAnswer = quizData.quizAnswers[0];
        const answerText = Array.isArray(firstAnswer.answer) ? firstAnswer.answer.join(', ') : firstAnswer.answer;
        userExample = `"${answerText}"`;
        
        // Detect main concern from answers
        const allAnswersText = quizData.quizAnswers.map(qa => 
          Array.isArray(qa.answer) ? qa.answer.join(' ') : qa.answer
        ).join(' ').toLowerCase();
        
        if (allAnswersText.includes('anxiety') || allAnswersText.includes('anxious') || allAnswersText.includes('worry')) {
          specificConcern = 'anxiety';
        } else if (allAnswersText.includes('depression') || allAnswersText.includes('sad') || allAnswersText.includes('down')) {
          specificConcern = 'depression';
        } else if (allAnswersText.includes('relationship') || allAnswersText.includes('partner')) {
          specificConcern = 'relationships';
        } else if (allAnswersText.includes('stress') || allAnswersText.includes('overwhelm')) {
          specificConcern = 'stress';
        }
      }
      
      setPersonalizedPlan({
        personalInsights: `When you shared ${userExample}, I could see the depth of what you're experiencing. Your willingness to open up about ${specificConcern} shows incredible courage and self-awareness - two qualities that are essential for transformation.`,
        transformationVision: `I envision you breaking free from the patterns that have kept you stuck with ${specificConcern}. You'll wake up feeling genuinely peaceful, confident in your ability to handle whatever comes your way, and excited about the person you're becoming.`,
        breakthroughTimeline: [
          `Week 1-2: You'll feel immediate relief as we address the ${specificConcern} that's been weighing on you most`,
          "Week 3-4: A major shift happens - you'll notice you're responding differently to triggers", 
          "Month 2: New, healthier patterns become your natural way of being",
          "Month 3+: You'll look back amazed at how far you've come and excited for what's ahead"
        ],
        destinyMessage: `Based on your responses, you have the self-awareness and inner strength that are the foundation of all lasting transformation. Your journey with ${specificConcern} is about to become your greatest source of wisdom and power.`,
        ttsContent: `When you shared ${userExample}, I could see the depth of what you're experiencing. I envision you breaking free from these patterns and stepping into genuine peace and confidence. Your breakthrough timeline: Week 1-2: Immediate relief, Week 3-4: Major shift happens, Month 2: New patterns become natural, Month 3+: Complete transformation. This isn't just possible for you - it's your inevitable next chapter.`
      });
      
      // Pre-generate audio for fallback plan too
      const fallbackPlan = {
        ttsContent: `When you shared ${userExample}, I could see the depth of what you're experiencing. I envision you breaking free from these patterns and stepping into genuine peace and confidence. Your breakthrough timeline: Week 1-2: Immediate relief, Week 3-4: Major shift happens, Month 2: New patterns become natural, Month 3+: Complete transformation. This isn't just possible for you - it's your inevitable next chapter.`
      };
      
      console.log('ℹ️ Skipping audio pre-generation for fallback plan - will generate on-demand');
      setPersonalizedPlan(fallbackPlan);
    } finally {
      setPlanLoading(false);
    }
  };

  // Generate personalized plan - restored to original working logic
  useEffect(() => {
    console.log('🔍 Plan generation check:', {
      hasQuizAnswers: !!quizData?.quizAnswers,
      quizAnswersLength: quizData?.quizAnswers?.length,
      personalizedPlan: !!personalizedPlan,
      planLoading,
      statusLoading,
      subscriptionIsActive: subscriptionStatus?.isActive,
      subscriptionStatus: subscriptionStatus?.status
    });
    
    // Wait for subscription status to load
    if (statusLoading) {
      console.log('⏳ Waiting for subscription status to load...');
      return;
    }
    
    // Ensure we have subscription status
    if (!subscriptionStatus) {
      console.log('⏳ Subscription status not loaded yet, waiting...');
      return;
    }

    // Additional check: if we don't have quiz data from navigation state, 
    // wait a bit for sessionStorage to be available after navigation
    if (!location.state?.quizData && !sessionStorage.getItem('completedQuizData')) {
      console.log('⏳ No quiz data in session, checking if user has completed quiz before...');
      
      // If user has completed quiz before (from profile) but no session data,
      // we should still show the subscribe page but skip plan generation
      if (profile?.quiz_completed) {
        console.log('✅ User has completed quiz before, showing subscribe page without plan generation');
        // Don't return here - let the component render the subscribe page
      } else {
        console.log('⚠️ User has not completed quiz, should redirect to quiz');
        // Don't return here either - let the normal flow handle the redirect
      }
    }

    // Check if user has active subscription
    const hasActiveSubscription = subscriptionStatus?.isActive === true || 
                                 subscriptionStatus?.status === 'active';
    
    console.log('🔍 Subscription check result:', {
      hasActiveSubscription,
      isActive: subscriptionStatus?.isActive,
      status: subscriptionStatus?.status
    });

    // Generate plan if conditions are met
    if (quizData?.quizAnswers?.length > 0 && !personalizedPlan && !planLoading && !hasActiveSubscription) {
      console.log('🎯 Starting personalized plan generation - all conditions met');
      generatePersonalizedPlan();
    } else if (hasActiveSubscription) {
      console.log('✅ User has active subscription - skipping personalized plan generation');
    } else if (!quizData?.quizAnswers?.length && profile?.quiz_completed) {
      console.log('ℹ️ User completed quiz before but no session data - showing subscribe page without personalized plan');
    } else {
      console.log('⚠️ Not generating plan - conditions not met:', {
        hasQuizAnswers: !!quizData?.quizAnswers,
        quizAnswersLength: quizData?.quizAnswers?.length,
        hasPersonalizedPlan: !!personalizedPlan,
        planLoading,
        hasActiveSubscription,
        profileQuizCompleted: profile?.quiz_completed
      });
    }
  }, [
    quizData?.quizAnswers?.length, 
    personalizedPlan, 
    planLoading, 
    statusLoading, 
    subscriptionStatus?.isActive, 
    subscriptionStatus?.status, 
    profile?.quiz_completed,
    location.state?.quizData
  ]);

  // Auto-redirect users with active subscriptions to conversation after showing the "You're all set!" message
  useEffect(() => {
    const hasActiveSubscription = subscriptionStatus?.isActive === true || 
                                 subscriptionStatus?.status === 'active';
    
    if (hasActiveSubscription && !statusLoading) {
      const timer = setTimeout(() => {
        console.log('🚀 Auto-redirecting user with active subscription to conversation');
        navigate('/conversation');
      }, 3000); // 3 second delay to show the "You're all set!" message briefly
      
      return () => clearTimeout(timer);
    }
  }, [subscriptionStatus?.isActive, subscriptionStatus?.status, statusLoading, navigate]);

  // Handle back navigation with subscription check
  const handleBackNavigation = () => {
    const hasActiveSubscription = subscriptionStatus?.isActive === true || 
                                 subscriptionStatus?.status === 'active';
    
    console.log('🔙 Back navigation requested:', {
      hasActiveSubscription,
      currentPath: location.pathname,
      hasHistory: window.history.length > 1
    });
    
    if (hasActiveSubscription) {
      // User has active subscription, allow navigation to conversation
      console.log('✅ User has active subscription - navigating to conversation');
      navigate('/conversation');
    } else {
      // User doesn't have active subscription, prevent access to conversation
      // Try to go back safely or redirect to home
      console.log('⚠️ User without subscription attempting to navigate back');
      
      // Check if there's navigation state indicating where they came from
      const fromPath = location.state?.from;
      
      if (fromPath && fromPath !== '/conversation') {
        // Safe to go back to the previous path
        console.log('🏠 Navigating back to safe path:', fromPath);
        navigate(fromPath);
      } else if (window.history.length > 1) {
        // Try browser back, but this is less reliable
        console.log('🔙 Attempting browser back navigation');
        window.history.back();
      } else {
        // No safe history, go to home
        console.log('🏠 No safe navigation history - redirecting to home');
        navigate('/');
      }
    }
  };

  // Cleanup React TTS service on unmount  
  useEffect(() => {
    return () => {
      if (speechSynthesis.current?.checkSupport()) {
        speechSynthesis.current.stop();
      }
      setIsPlaying(false);
      setIsSpeakingPlan(false);
      setAudioLoading(false);
      setAudioGenerationInProgress(false);
    };
  }, []);

  const handlePlayAudio = async () => {
    console.log('🔥 REACT TTS - handlePlayAudio called with states:', {
      hasPersonalizedPlan: !!personalizedPlan,
      hasTtsContent: !!personalizedPlan?.ttsContent,
      ttsContentLength: personalizedPlan?.ttsContent?.length || 0,
      isPlaying,
      isSpeakingPlan,
      reactTtsSupported,
      audioGenerationInProgress
    });

    // Prevent multiple simultaneous calls
    if (audioGenerationInProgress || isSpeakingPlan) {
      console.log('🚫 Audio already in progress, ignoring click');
      return;
    }

    if (!personalizedPlan?.ttsContent) {
      console.log('❌ No TTS content available:', {
        personalizedPlan: !!personalizedPlan,
        ttsContent: personalizedPlan?.ttsContent ? 'Present' : 'Missing'
      });
      alert('Audio content is not available yet. Please wait for your plan to finish generating.');
      return;
    }

    if (!reactTtsSupported) {
      alert('Text-to-speech is not supported in your browser. Please try using a different browser.');
      return;
    }
    
    try {
      // If currently playing, stop
      if (isPlaying || isSpeakingPlan) {
        console.log('🛑 Stopping React TTS playback');
        if (speechSynthesis.current?.checkSupport()) {
          speechSynthesis.current.stop();
        }
        setIsPlaying(false);
        setIsSpeakingPlan(false);
        setAudioGenerationInProgress(false);
        return;
      }

      console.log('🚀 Starting React TTS playback for personal plan');
      setAudioGenerationInProgress(true);
      setAudioLoading(true);
      
      // Use React TTS with a female voice
      if (speechSynthesis.current?.checkSupport()) {
        // Set voice to female if available
        speechSynthesis.current.setVoiceByGender('female');
        
        // Speak the personalized plan content
        await speechSynthesis.current.speak(personalizedPlan.ttsContent);
        console.log('✅ React TTS playback initiated successfully');
      } else {
        throw new Error('Speech synthesis not supported');
      }
      
    } catch (error) {
      console.error('❌ Error with React TTS playback:', error);
      setAudioLoading(false);
      setIsPlaying(false);
      setIsSpeakingPlan(false);
      setAudioGenerationInProgress(false);
      
      alert('Unable to play audio. Please check your browser settings and try again.');
    }
  };

  const plans = [
    {
      id: 'yearly',
      name: 'Yearly Plan',
      price: 120,
      monthlyPrice: 10,
      dailyPrice: 0.33, // $120/365 days
      tokens: 300,
      tokensFrequency: 'monthly',
      color: '#4CAF50',
      gradient: 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
      icon: <EmojiEvents sx={{ fontSize: 40 }} />,
      description: 'Most people choose this - incredible value!',
      features: [
        '🌟 Unlimited breakthrough conversations',
        '💎 300 premium tokens monthly (accumulate)',
        '🔄 Unused tokens roll over - no waste',
        '💰 Buy extra tokens anytime at 50% off',
        '🎵 Premium therapeutic voice options',
        '⚡ Priority support & lightning-fast responses',
        '📊 Advanced progress tracking & insights',
        '🎁 Exclusive content & breakthrough techniques',
        '⏰ Cancel anytime, keep your progress'
      ],
      savings: '💰 Save $59.88 vs Monthly (67% off!)',
      badge: '🔥 MOST POPULAR',
      popular: true
    },
    {
      id: 'monthly',
      name: 'Monthly Plan',
      price: 14.99,
      monthlyPrice: 14.99,
      dailyPrice: 0.50, // $14.99/30 days
      tokens: 300,
      tokensFrequency: 'monthly',
      color: '#2196F3',
      gradient: 'linear-gradient(135deg, #2196F3 0%, #21CBF3 100%)',
      icon: <FlashOn sx={{ fontSize: 40 }} />,
      description: 'Flexible monthly subscription',
      features: [
        '300 tokens every month (accumulate)',
        'Unused tokens roll over monthly',
        'Purchase additional tokens anytime',
        'Premium voice options',
        'Standard support',
        'Basic wellness tracking',
        'Cancel anytime'
      ],
      savings: null,
      badge: null,
      popular: false
    }
  ];

  const handlePlanSelect = (planId) => {
    setSelectedPlan(planId);
  };

  const handleSubscribe = async () => {
    const selectedPlanData = plans.find(plan => plan.id === selectedPlan);
    setLoading(true);
    setError(null);
    
    try {
      let result;
      if (selectedPlan === 'yearly') {
        // Store subscription type in sessionStorage for PaymentSuccess component
        sessionStorage.setItem('selectedSubscriptionType', 'yearly');
        console.log('💾 Stored subscription type in sessionStorage:', 'yearly');
        
        // Create yearly subscription
        result = await subscriptionService.createYearlySubscription();
      } else {
        // Store subscription type in sessionStorage for PaymentSuccess component
        sessionStorage.setItem('selectedSubscriptionType', 'monthly');
        console.log('💾 Stored subscription type in sessionStorage:', 'monthly');
        
        // Create monthly subscription
        result = await subscriptionService.createMonthlySubscription();
      }
      
      // The service will automatically redirect to Stripe Checkout
      // If we reach here, there might be an issue
      if (!result.sessionUrl) {
        throw new Error('Failed to create checkout session');
      }
    } catch (error) {
      console.error('Subscription error:', error);
      setError(error.message || 'Failed to create subscription. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  // Show current subscription status if user already has one
  if (statusLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 4, textAlign: 'center' }}>
        <CircularProgress size={60} />
        <Typography variant="h6" sx={{ mt: 2 }}>Loading subscription status...</Typography>
      </Container>
    );
  }

  console.log('🔍 Subscribe render check:', {
    isActive: subscriptionStatus?.isActive,
    status: subscriptionStatus?.status,
    shouldShowActiveSubscription: subscriptionStatus?.isActive && subscriptionStatus?.status === 'active',
    fullSubscriptionStatus: subscriptionStatus
  });

  // Check if user has active subscription (multiple ways to be sure)
  const hasActiveSubscription = subscriptionStatus?.isActive === true || 
                               subscriptionStatus?.status === 'active';

  console.log('🔍 Subscribe component: Detailed subscription check:', {
    hasActiveSubscription,
    isActive: subscriptionStatus?.isActive,
    status: subscriptionStatus?.status,
    fullSubscriptionStatus: subscriptionStatus
  });

  if (hasActiveSubscription) {
    console.log('✅ Rendering active subscription UI - Continue Conversation button');
    
    return (
      <Container maxWidth="md" sx={{ py: 4 }}>
        <Box sx={{ textAlign: 'center', mb: 4 }}>
          <IconButton onClick={handleBackNavigation} sx={{ mr: 2 }}>
            <ArrowBack />
          </IconButton>
          <CheckCircle sx={{ fontSize: 60, color: 'success.main', mb: 2 }} />
          <Typography variant="h4" sx={{ fontWeight: 'bold', mb: 2 }}>
            You're all set! 🎉
          </Typography>
          <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
            You already have an active subscription. Continue your wellness journey!
          </Typography>
          <Button 
            variant="contained" 
            size="large" 
            onClick={() => {
              console.log('🎯 Continue Conversation clicked - navigating to /conversation');
              navigate('/conversation');
            }}
            disabled={!hasActiveSubscription}
            sx={{ mb: 2 }}
          >
            Continue Conversation
          </Button>
          <br />
          <Button 
            variant="outlined" 
            onClick={() => navigate('/settings')}
          >
            Manage Subscription
          </Button>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: { xs: 2, sm: 3 }, px: { xs: 1, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ 
        display: 'flex', 
        alignItems: 'center', 
        mb: { xs: 3, sm: 4 },
        gap: { xs: 1, sm: 2 },
        flexDirection: { xs: 'column', sm: 'row' },
        textAlign: { xs: 'center', sm: 'left' }
      }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: { xs: 1, sm: 2 } }}>
          <IconButton onClick={handleBackNavigation} 
                     sx={{ mr: { xs: 1, sm: 2 }, width: { xs: 36, sm: 40 }, height: { xs: 36, sm: 40 } }}>
            <ArrowBack sx={{ fontSize: { xs: 18, sm: 20 } }} />
          </IconButton>
          <Psychology sx={{ color: '#667eea', fontSize: { xs: 20, sm: 24, md: 32 } }} />
        </Box>
        <Typography variant="h4" sx={{ 
          fontWeight: 'bold', 
          color: 'primary.main',
          fontSize: { xs: '1.25rem', sm: '1.5rem', md: '2.125rem' }
        }}>
          Choose Your Plan
        </Typography>
      </Box>

      {/* Hero Section */}
      <Paper 
        sx={{ 
          p: { xs: 3, sm: 4 }, 
          mb: { xs: 3, sm: 4 }, 
          background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
          color: 'white',
          textAlign: 'center',
          borderRadius: { xs: 2, sm: 3 },
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        <Box sx={{ position: 'relative', zIndex: 1 }}>
          <Typography variant="h3" sx={{ 
            fontWeight: 'bold', 
            mb: { xs: 1.5, sm: 2 },
            fontSize: { xs: '1.5rem', sm: '2rem', md: '2.5rem', lg: '3rem' }
          }}>
            🚀 Your Transformation Starts Now
          </Typography>
          <Typography variant="h6" sx={{ 
            opacity: 0.9,
            maxWidth: '600px',
            mx: 'auto',
            fontSize: { xs: '0.9rem', sm: '1rem', md: '1.25rem' },
            lineHeight: { xs: 1.4, sm: 1.5 }
          }}>
            Join thousands who've already transformed their lives with AI-powered breakthrough therapy
          </Typography>
        </Box>
      </Paper>

      {/* Plan Generation Loading State */}
      {planLoading && (
        <Fade in>
          <Paper 
            sx={{ 
              p: { xs: 4, sm: 6 }, 
              mb: { xs: 4, sm: 6 }, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 4,
              textAlign: 'center',
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)'
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              <CircularProgress sx={{ color: 'white', mb: 3 }} size={60} />
              <Typography variant="h4" sx={{ 
                fontWeight: 'bold',
                fontSize: { xs: '1.5rem', sm: '2rem' },
                mb: 2,
                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                backgroundClip: 'text',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
              }}>
                ✨ Creating Your Personal Transformation Plan
              </Typography>
              <Typography variant="h6" sx={{ 
                opacity: 0.9,
                maxWidth: '500px',
                mx: 'auto',
                fontSize: { xs: '1rem', sm: '1.1rem' },
                lineHeight: 1.5
              }}>
                Our AI is analyzing your quiz responses to craft a breakthrough plan specifically for you...
              </Typography>
              <Box sx={{ mt: 3 }}>
                <Typography variant="body2" sx={{ 
                  opacity: 0.8,
                  fontStyle: 'italic'
                }}>
                  This will just take a moment - your plan will be worth the wait! 🎯
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Fade>
      )}
      {console.log('🎨 RENDER STATE CHECK - Plan Loading Box:', {
        planLoading,
        shouldShowPlanLoading: planLoading,
        personalizedPlan: !!personalizedPlan,
        audioLoading
      })}

      {/* Personalized Plan Section */}
      {personalizedPlan && (
        <Fade in>
          <Paper 
            sx={{ 
              p: { xs: 4, sm: 6 }, 
              mb: { xs: 4, sm: 6 }, 
              background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
              color: 'white',
              borderRadius: 4,
              position: 'relative',
              overflow: 'hidden',
              boxShadow: '0 20px 60px rgba(102, 126, 234, 0.3)',
              '&::before': {
                content: '""',
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                bottom: 0,
                background: 'radial-gradient(circle at 20% 50%, rgba(255,255,255,0.1) 0%, transparent 50%)',
                pointerEvents: 'none'
              }
            }}
          >
            <Box sx={{ position: 'relative', zIndex: 1 }}>
              {/* Header with TTS */}
              <Box sx={{ 
                display: 'flex', 
                alignItems: 'center', 
                justifyContent: 'space-between', 
                mb: 5,
                flexDirection: { xs: 'column', sm: 'row' },
                gap: { xs: 2, sm: 0 }
              }}>
                <Box sx={{ textAlign: { xs: 'center', sm: 'left' } }}>
                  <Typography variant="h3" sx={{ 
                    fontWeight: 'bold',
                    fontSize: { xs: '2rem', sm: '2.5rem' },
                    mb: 1,
                    background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                    backgroundClip: 'text',
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent',
                    filter: 'drop-shadow(0 2px 4px rgba(0,0,0,0.3))'
                  }}>
                    💝 Your Personal Transformation Plan
                  </Typography>
                  <Typography variant="subtitle1" sx={{ 
                    opacity: 0.9,
                    fontStyle: 'italic',
                    fontSize: '1.1rem'
                  }}>
                    Crafted specifically for you by Sarah
                  </Typography>
                </Box>
                
                {/* Audio Loading Status */}
                {audioLoading && (
                  <Box sx={{ 
                    display: 'flex', 
                    alignItems: 'center', 
                    justifyContent: 'center',
                    gap: 1,
                    mt: 2,
                    mb: -2
                  }}>
                    <CircularProgress size={16} sx={{ color: 'rgba(255,255,255,0.8)' }} />
                    <Typography variant="body2" sx={{ 
                      color: 'rgba(255,255,255,0.8)',
                      fontSize: '0.9rem',
                      fontStyle: 'italic'
                    }}>
                      Creating your personalized audio experience...
                    </Typography>
                  </Box>
                )}
                
                <Tooltip title={
                  !reactTtsSupported ? "Text-to-speech not supported in this browser" :
                  audioLoading ? "Creating your personalized audio experience..." : 
                  isPlaying ? "Stop listening" : 
                  "Hear your personalized plan read aloud"
                }>
                  <IconButton 
                    onClick={handlePlayAudio}
                    disabled={!personalizedPlan.ttsContent || !reactTtsSupported}
                    sx={{ 
                      color: 'white', 
                      backgroundColor: !reactTtsSupported ? 'rgba(158,158,158,0.5)' : audioLoading ? 'rgba(255,152,0,0.8)' : isPlaying ? 'rgba(244,67,54,0.8)' : 'rgba(33,150,243,0.8)',
                      backdropFilter: 'blur(10px)',
                      border: '2px solid rgba(255,255,255,0.2)',
                      width: 64,
                      height: 64,
                      '&:hover': { 
                        backgroundColor: !reactTtsSupported ? 'rgba(158,158,158,0.5)' : audioLoading ? 'rgba(255,152,0,0.9)' : isPlaying ? 'rgba(211,47,47,0.9)' : 'rgba(25,118,210,0.9)',
                        transform: !reactTtsSupported ? 'none' : 'scale(1.05)'
                      },
                      transition: 'all 0.3s ease',
                      cursor: !reactTtsSupported ? 'not-allowed' : 'pointer'
                    }}
                  >
                    {audioLoading ? (
                      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
                        <CircularProgress size={24} sx={{ color: 'white', mb: 0.5 }} />
                        <Typography variant="caption" sx={{ fontSize: '0.6rem', color: 'white', opacity: 0.8 }}>
                          Creating...
                        </Typography>
                      </Box>
                    ) : !reactTtsSupported ? (
                      <VolumeOff sx={{ fontSize: 32, opacity: 0.6 }} />
                    ) : isPlaying ? (
                      <Pause sx={{ fontSize: 32 }} />
                    ) : (
                      <PlayArrow sx={{ fontSize: 32 }} />
                    )}
                  </IconButton>
                </Tooltip>
                
                {/* Caption about professional voices */}
                <Typography variant="caption" sx={{ 
                  color: 'rgba(255,255,255,0.7)', 
                  textAlign: 'center',
                  mt: 1,
                  fontSize: '0.8rem',
                  fontStyle: 'italic'
                }}>
                  Professional human voices available with subscription
                </Typography>
              </Box>

              <Grid container spacing={4}>
                {/* Personal Insights - Hidden on Subscribe Page */}
                {/* <Grid item xs={12}>
                  <Card sx={{ 
                    background: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 3,
                    mb: 3
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 'bold', 
                        mb: 2, 
                        display: 'flex', 
                        alignItems: 'center',
                        color: '#FFD700'
                      }}>
                        <Psychology sx={{ mr: 2, fontSize: 32 }} /> I See You
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        lineHeight: 1.8, 
                        fontSize: '1.1rem',
                        color: 'rgba(255,255,255,0.95)'
                      }}>
                        {personalizedPlan.personalInsights}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid> */}

                {/* Transformation Vision */}
                <Grid item xs={12}>
                  <Card sx={{ 
                    background: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 3,
                    mb: 3
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 'bold', 
                        mb: 2, 
                        display: 'flex', 
                        alignItems: 'center',
                        color: '#FFD700'
                      }}>
                        <AutoAwesome sx={{ mr: 2, fontSize: 32 }} /> Your Transformation Vision
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        lineHeight: 1.8, 
                        fontSize: '1.1rem',
                        color: 'rgba(255,255,255,0.95)'
                      }}>
                        {personalizedPlan.transformationVision}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Breakthrough Timeline */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    background: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 3,
                    height: '100%'
                  }}>
                    <CardContent sx={{ p: 4 }}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 'bold', 
                        mb: 3, 
                        display: 'flex', 
                        alignItems: 'center',
                        color: '#FFD700'
                      }}>
                        <TrendingUp sx={{ mr: 2, fontSize: 32 }} /> Your Breakthrough Timeline
                      </Typography>
                      <List dense sx={{ '& .MuiListItem-root': { py: 1 } }}>
                        {personalizedPlan.breakthroughTimeline?.map((milestone, index) => (
                          <ListItem key={index} sx={{ px: 0, alignItems: 'flex-start' }}>
                            <ListItemIcon sx={{ minWidth: 40, mt: 0.5 }}>
                              <Box sx={{ 
                                width: 24, 
                                height: 24, 
                                borderRadius: '50%',
                                background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                fontSize: '0.8rem',
                                fontWeight: 'bold',
                                color: 'white'
                              }}>
                                {index + 1}
                              </Box>
                            </ListItemIcon>
                            <ListItemText 
                              primary={milestone}
                              primaryTypographyProps={{ 
                                variant: 'body2', 
                                sx: { 
                                  lineHeight: 1.6, 
                                  color: 'rgba(255,255,255,0.95)',
                                  fontSize: '1rem'
                                } 
                              }}
                            />
                          </ListItem>
                        ))}
                      </List>
                    </CardContent>
                  </Card>
                </Grid>

                {/* Destiny Message */}
                <Grid item xs={12} md={6}>
                  <Card sx={{ 
                    background: 'rgba(255,255,255,0.1)', 
                    backdropFilter: 'blur(10px)',
                    border: '1px solid rgba(255,255,255,0.2)',
                    borderRadius: 3,
                    height: '100%'
                  }}>
                    <CardContent sx={{ p: 4, display: 'flex', flexDirection: 'column', justifyContent: 'center' }}>
                      <Typography variant="h5" sx={{ 
                        fontWeight: 'bold', 
                        mb: 3, 
                        display: 'flex', 
                        alignItems: 'center',
                        color: '#FFD700'
                      }}>
                        <EmojiEvents sx={{ mr: 2, fontSize: 32 }} /> Your Destiny
                      </Typography>
                      <Typography variant="body1" sx={{ 
                        lineHeight: 1.8, 
                        fontSize: '1.1rem',
                        color: 'rgba(255,255,255,0.95)'
                      }}>
                        {personalizedPlan.destinyMessage}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              </Grid>

              {/* Call to Action */}
              <Box sx={{ 
                mt: 6, 
                p: 4, 
                background: 'rgba(255,255,255,0.15)', 
                backdropFilter: 'blur(10px)',
                borderRadius: 3,
                textAlign: 'center',
                border: '2px solid rgba(255,255,255,0.2)'
              }}>
                <Typography variant="h4" sx={{ 
                  fontWeight: 'bold', 
                  mb: 2,
                  background: 'linear-gradient(135deg, #FFD700 0%, #FFA500 100%)',
                  backgroundClip: 'text',
                  WebkitBackgroundClip: 'text',
                  WebkitTextFillColor: 'transparent'
                }}>
                  Your transformation starts with a single choice. 
                </Typography>
                <Typography variant="h6" sx={{ 
                  opacity: 0.95,
                  mb: 2
                }}>
                  Choose your plan below and step into the life you deserve! ✨
                </Typography>
                <Typography variant="body2" sx={{ 
                  opacity: 0.8,
                  fontStyle: 'italic'
                }}>
                  "The best time to plant a tree was 20 years ago. The second best time is now."
                </Typography>
              </Box>
            </Box>
          </Paper>
        </Fade>
      )}

      {/* Error Alert */}
      {error && (
        <Alert 
          severity="error" 
          sx={{ mb: 3 }}
          onClose={() => setError(null)}
        >
          {error}
        </Alert>
      )}

      {/* Subscription Plans */}
      <Grid container spacing={3} sx={{ mb: 4 }}>
        {plans.map((plan, index) => (
          <Grid item xs={12} md={6} key={plan.id}>
            <Zoom in style={{ transitionDelay: `${index * 100}ms` }}>
              <Card
                sx={{
                  height: '100%',
                  cursor: 'pointer',
                  transition: 'all 0.3s ease-in-out',
                  transform: selectedPlan === plan.id ? 'scale(1.05)' : 'scale(1)',
                  border: selectedPlan === plan.id ? '3px solid' : '2px solid transparent',
                  borderColor: selectedPlan === plan.id ? plan.color : 'transparent',
                  boxShadow: selectedPlan === plan.id ? `0 8px 32px ${plan.color}30` : 2,
                  position: 'relative',
                  background: selectedPlan === plan.id ? `${plan.gradient}, rgba(255,255,255,0.95)` : 'white',
                  backgroundBlendMode: selectedPlan === plan.id ? 'overlay' : 'normal',
                  '&:hover': {
                    transform: 'scale(1.02)',
                    boxShadow: `0 8px 32px ${plan.color}20`,
                  }
                }}
                onClick={() => handlePlanSelect(plan.id)}
              >
                {plan.popular && (
                  <Chip
                    label={plan.badge}
                    sx={{
                      position: 'absolute',
                      top: 16,
                      right: 16,
                      background: plan.gradient,
                      color: 'white',
                      fontWeight: 'bold',
                      zIndex: 1
                    }}
                  />
                )}
                
                <CardContent sx={{ p: 3, height: '100%', display: 'flex', flexDirection: 'column' }}>
                  <Box sx={{ textAlign: 'center', mb: 3 }}>
                    <Avatar
                      sx={{
                        bgcolor: plan.color,
                        width: 80,
                        height: 80,
                        mx: 'auto',
                        mb: 2,
                        background: plan.gradient
                      }}
                    >
                      {plan.icon}
                    </Avatar>
                    
                    <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                      {plan.name}
                    </Typography>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {plan.description}
                    </Typography>

                    <Box sx={{ mb: 2 }}>
                      {/* Daily Price - Main Display */}
                      <Typography variant="h2" sx={{ 
                        fontWeight: 'bold', 
                        color: 'white',
                        fontSize: { xs: '2.5rem', sm: '3rem' },
                        textShadow: `
                          -1px -1px 0 #000,
                          1px -1px 0 #000,
                          -1px 1px 0 #000,
                          1px 1px 0 #000,
                          -2px 0 0 #000,
                          2px 0 0 #000,
                          0 -2px 0 #000,
                          0 2px 0 #000
                        `,
                        WebkitTextStroke: '1px black'
                      }}>
                        ${plan.dailyPrice.toFixed(2)}
                      </Typography>
                      <Typography variant="h6" color="text.secondary" sx={{ fontWeight: 'bold' }}>
                        per day
                      </Typography>
                      
                      {/* Actual Subscription Cost - Secondary */}
                      <Typography variant="body2" color="text.secondary" sx={{ mt: 1, opacity: 0.7 }}>
                        Billed {plan.id === 'yearly' ? `$${plan.price}/year` : `$${plan.price}/month`}
                        {plan.id === 'yearly' && (
                          <Typography variant="caption" display="block">
                            (${plan.monthlyPrice}/month equivalent)
                          </Typography>
                        )}
                      </Typography>
                    </Box>

                    {plan.savings && (
                      <Chip
                        label={plan.savings}
                        sx={{
                          bgcolor: 'success.light',
                          color: 'success.contrastText',
                          fontWeight: 'bold',
                          mb: 2
                        }}
                      />
                    )}
                  </Box>

                  <List sx={{ flex: 1 }}>
                    {plan.features.map((feature, idx) => (
                      <ListItem key={idx} sx={{ py: 0.5, px: 0 }}>
                        <ListItemIcon>
                          <CheckCircle sx={{ color: plan.color, fontSize: 20 }} />
                        </ListItemIcon>
                        <ListItemText 
                          primary={feature} 
                          primaryTypographyProps={{ 
                            variant: 'body2',
                            sx: { fontWeight: feature.includes('FREE') ? 'bold' : 'normal' }
                          }} 
                        />
                      </ListItem>
                    ))}
                  </List>
                </CardContent>
              </Card>
            </Zoom>
          </Grid>
        ))}
      </Grid>

      {/* Subscribe Button */}
      <Box sx={{ textAlign: 'center', mb: 4 }}>
        <Button
          onClick={handleSubscribe}
          variant="contained"
          size="large"
          disabled={loading}
          sx={{
            background: plans.find(p => p.id === selectedPlan)?.gradient || 'linear-gradient(135deg, #4CAF50 0%, #8BC34A 100%)',
            color: 'white',
            fontSize: { xs: '1rem', sm: '1.2rem' },
            fontWeight: 'bold',
            py: { xs: 1.5, sm: 2 },
            px: { xs: 3, sm: 6 },
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.2)',
            minWidth: { xs: 280, sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            maxWidth: { xs: '100%', sm: 'none' },
            '&:hover': {
              transform: 'translateY(-2px)',
              boxShadow: '0 12px 40px rgba(0,0,0,0.3)',
            },
            transition: 'all 0.3s ease-in-out'
          }}
          startIcon={loading ? <CircularProgress size={20} color="inherit" /> : <AutoAwesome />}
        >
          {loading ? 'Processing...' : `Subscribe to ${plans.find(p => p.id === selectedPlan)?.name}`}
        </Button>
        
        <Typography variant="caption" display="block" sx={{ mt: 2, color: 'text.secondary' }}>
          Secure payment processed by Stripe. Cancel anytime.
        </Typography>
      </Box>

      {/* Trust & Security */}
      <Paper sx={{ p: 3, backgroundColor: 'rgba(0,0,0,0.02)' }}>
        <Typography variant="h6" sx={{ fontWeight: 'bold', mb: 2, textAlign: 'center' }}>
          Why Choose Sanity Reps?
        </Typography>
        <Grid container spacing={3} sx={{ textAlign: 'center' }}>
          <Grid item xs={12} sm={4}>
            <Security sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Secure & Private
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Your conversations are encrypted and private. We never share your data.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <Support sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              24/7 Support
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Get help whenever you need it with our responsive support team.
            </Typography>
          </Grid>
          <Grid item xs={12} sm={4}>
            <AllInclusive sx={{ fontSize: 40, color: 'primary.main', mb: 1 }} />
            <Typography variant="subtitle1" sx={{ fontWeight: 'bold', mb: 1 }}>
              Cancel Anytime
            </Typography>
            <Typography variant="body2" color="text.secondary">
              No long-term commitments. Cancel your subscription anytime.
            </Typography>
          </Grid>
        </Grid>
      </Paper>
    </Container>
  );
};

export default Subscribe;
